import {IProduct} from "../../interfaces/IProduct";
import React, {useEffect, useState} from "react";
import styled from "styled-components";
import AccordionSection from "../AccordionSection";
import ProductReviews from "./ProductReviews";
import {useMessages} from "../../hooks/useMessages";
import ProductAttributesSection from "./ProductAttributesSection";
import {IRelatedProducts} from "../../interfaces/IRelatedProducts";
import ProductCarousel from "./ProductCarousel";
import DetailProperties from "./DetailProperties";
import parse from "html-react-parser";
import {IProductPriceHistory} from "../../interfaces/IProductPriceHistory";
import ProductPriceHistory from "./ProductPriceHistory";
import {useQuery} from "@tanstack/react-query";
import {request} from "../../shared/httpInterceptor";
import {useCountryLang} from "../../hooks/useCountryLang";
import {getTheme} from "../../shared/themeUtils";
import {useRouter} from "next/router";
import Script from "next/script";
import {IProductPart} from "../../interfaces/IProductParts";
import ProductPart from "./ProductPart";
import {Col, Row} from "react-bootstrap";
import {IMagazineEntry} from "../../interfaces/IMagazine";
import MagazineListingEntry from "../magazine/MagazineListingEntry";

const SectionsContain = styled.div`
    .notice-section {
        margin-bottom: ${getTheme("space.2")};
        background: ${getTheme("colors.backgroundOrange")};
        color: ${getTheme("colors.textOrange")};
        padding: ${getTheme("space.2")};
        @media (max-width: ${getTheme("breakpoints.2")}) {
            margin-top: ${getTheme("space.2")};
        }

        ul {
            padding: 0;
            margin: 0;

            li {
                padding: 0;
                margin: 0;
            }
        }
    }

    .short-description {
        padding: ${getTheme("space.2")};
        margin: ${getTheme("space.2")} 0;
        background: ${getTheme("colors.background")};
        border-radius: ${getTheme("borderRadius")};
    }

    .parts {
        margin-top: ${getTheme("space.2")};
        display: flex;
        align-items: center;
    }

    .properties-list {
    list-style: none;
    padding: 0;
  }

  .warnings-list {
      list-style: none;
      padding: 0;
  }

  .warnings-list li {
      margin-bottom: 0.5rem;
  }

  .description {
    white-space: pre-wrap;
  }
`

const FlatSection = styled.div`
  margin-top: ${getTheme("space.4")};
  padding-bottom: ${getTheme("space.2")};
  border-bottom: 1px solid ${getTheme("colors.border")};
  
  .section-title {
    font-size: ${getTheme("fontSizes.large")};
    font-weight: bold;
    margin: ${getTheme("space.1")} 0;
  }
`

interface ProductSectionsProps {
  product: IProduct,
  relatedProducts: IRelatedProducts | null,
  partsSection?: IProductPart[],
  suppliedItems?: string | null,
  magazineArticles: IMagazineEntry[] | undefined,
  successor: any,
  predecessor: any,
  sets?: IProductPart[]
}

const ProductSections = (props: ProductSectionsProps) => {
  const {product, relatedProducts, partsSection, suppliedItems, magazineArticles, predecessor, successor, sets} = props;
  const productId = product.productID
  const [descriptionTitle, setDescriptionTitle] = useState("Description");
  const [propertiesTitle, setPropertiesTitle] = useState("Properties");
  const [attributesTitle, setAttributesTitle] = useState("Attributes");
  const [relatedProductsTitle, setRelatedProductsTitle] = useState("Related Products");
  const [accessoryForProductsTitle, setAccessoryForProductsTitle] = useState("Accessories");
  const [priceHistoryTitle, setPriceHistoryTitle] = useState("Price History");
  const messagesQuery = useMessages()
  const countryLang = useCountryLang()
  const router = useRouter()

  const fetchPriceHistory = async (productId: number, countryLang: string | null, host?: string, cookies?: any) => {
    const res = await request({
      url: `/${countryLang}/product/${productId}/price/trend`,
      method: 'get',
      host: host,
      cookies: cookies
    });
    return res.data;
  }

  const priceHistoryQuery = useQuery<IProductPriceHistory>(["product-price-history", productId, countryLang],
    () => fetchPriceHistory(productId, countryLang), {
      enabled: countryLang != null && !isNaN(productId) && Number.isFinite(productId) && (product && product.showPriceTrendGraph != undefined),
    })

  console.log(product.showPriceTrendGraph)
  const processTextWithNewlines = (text: any) => {
    if (!text) return null;
    return text.split('\n').map((item: any, index: number) => (
      <React.Fragment key={`text-line-${index}`}>
        {parse(item)}
        <br/>
      </React.Fragment>
    ));
  };

  useEffect(() => {
    if (messagesQuery.data?.messages) {
      setDescriptionTitle(messagesQuery.data.messages["description"] || "Description");
      setPropertiesTitle(messagesQuery.data.messages["properties"] || "Properties");
      setAttributesTitle(messagesQuery.data.messages["attributes"] || "Attributes");
      setRelatedProductsTitle(messagesQuery.data.messages["related-products"] || "Related Products");
      setAccessoryForProductsTitle(messagesQuery.data.messages["accessory-for-these-products"] || "Accessories");
      setPriceHistoryTitle(messagesQuery.data.messages["price-history"] || "Price History");
    }
  }, [messagesQuery.data]);

  const getPropertyList = () => {
    if (!product.properties) return null;
    
    return <ul className="properties-list">
      {product.properties.map((e: string, index: number) => {
        if (e && e.length > 0) {
          return <li key={`property-${index}`}>{e}</li>
        }
        return null;
      })}
    </ul>
  }

  const parsedDescription = product.description ? parse(product.description) : null;
  
  return (
    <SectionsContain>
      {/* Notice. Warnings and important info */}
      {product.warnings && product.warnings.length > 0 &&
        <div className={"notice-section"}>
          <ul className="warnings-list">
            {product.warnings.map((e: string, index: number) => {
              if (e && e.length > 0) {
                return <li key={`warning-${index}`}>{e}</li>
              }
              return null;
            })}
          </ul>
        </div>
      }

      {product.shortDescription &&
        <div className="short-description">
          {product.shortDescription}
        </div>
      }

      {/* Parts */}
      {partsSection && partsSection.length > 0 && messagesQuery.data &&
        <AccordionSection id={"parts"} title={messagesQuery.data.messages["parts"] || "Parts"}>
          <div className="section-title">{messagesQuery.data.messages["set-is-composed"] || "Set Composition"}</div>
          <Row className="parts">
            {partsSection.map((entry, index) => (
              <ProductPart key={`part-${entry.productId || index}`} product={entry.product} quantity={entry.quantity}/>
            ))}
          </Row>
        </AccordionSection>
      }

      {/* Sets */}
      {sets && sets.length > 0 && messagesQuery.data &&
        <AccordionSection id={"sets"} title={messagesQuery.data.messages["sets"] || "Sets"}>
          <div className="section-title">{messagesQuery.data.messages["set-is-composed"] || "Set Composition"}</div>
          <Row className="parts">
            {sets.map((entry, index) => (
              <ProductPart key={`set-${entry.productId || index}`} product={entry.product} quantity={entry.quantity}/>
            ))}
          </Row>
        </AccordionSection>
      }

      {/* Description */}
      {product.description && 
        <AccordionSection id="description" title={descriptionTitle}>
          <div className="description">
            {parsedDescription}

            {product.showLoadbeeContent &&
              <div className="loadbeeTabContent"
                  data-loadbee-apikey="Nx8bLuhKvKkPXgyd8B65z36vWbJDwZt9"
                  data-loadbee-gtin={product.ean}
                  data-loadbee-gtintype="ean"
                  data-loadbee-locale={`${router.query.lang}_${router.query.country?.toString().toUpperCase()}`}
              ></div>
            }
          </div>

          {product.hasDetailProperties && product.detailProperties && <div className="detailed-properties">
            <DetailProperties properties={product.detailProperties}/>
          </div>}
        </AccordionSection>}

      {/* Supplied Items */}
      {suppliedItems && suppliedItems.length > 1 && messagesQuery.data &&
        <AccordionSection id="suppliedItems" title={messagesQuery.data.messages["supplied-items"] || "Supplied Items"}>
          <div>{parse(suppliedItems.replace(/\n/g, "<br />"))}</div>
        </AccordionSection>
      }

      {/* Properties */}
      {product.properties && product.properties.length > 0 && 
        <AccordionSection id="properties" title={propertiesTitle}>
          {getPropertyList()}
        </AccordionSection>}

      {/* Specifications */}
      {product.specifications && messagesQuery.data &&
        <AccordionSection id={"specifications"} title={messagesQuery.data.messages["specifications"] || "Specifications"}>
          <div>{processTextWithNewlines(product.specifications)}</div>
        </AccordionSection>
      }

      {/* Attributes */}
      {(product.attributes && product.attributes.length > 0) && 
        <AccordionSection id="attributes" title={attributesTitle}>
          <ProductAttributesSection product={product}/>
        </AccordionSection>}

      {/* Product Magazine Articles */}
      {magazineArticles && magazineArticles.length > 0 && messagesQuery.data &&
        <AccordionSection id={"productMagazineArticles"} title={messagesQuery.data.messages["magazine-page-title"]}>
          <p>{messagesQuery.data.messages["product-appears-in-magazine-desc"]}</p>

          <Row>
            {magazineArticles.map(entry => (
              <Col sm={6} lg={4} key={entry.articleID}>
                <MagazineListingEntry entry={entry}/>
              </Col>
            ))}
          </Row>
        </AccordionSection>
      }

      {/* Predecessor */}
      {predecessor && messagesQuery.data &&
        <AccordionSection id={"predecessor"} title={messagesQuery.data.messages["predecessor"]}>
          <ProductPart product={predecessor}/>
        </AccordionSection>
      }

      {/* Successor */}
      {successor && messagesQuery.data &&
        <AccordionSection id={"successor"} title={messagesQuery.data.messages["successor"]}>
          <ProductPart product={successor}/>
        </AccordionSection>
      }

      {product.showPriceTrendGraph && (priceHistoryQuery.data && priceHistoryQuery.data.datapoints && priceHistoryQuery.data.datapoints.length > 1) &&
        <AccordionSection id="priceHistory" title={priceHistoryTitle}>
          <ProductPriceHistory priceHistory={priceHistoryQuery.data} />
        </AccordionSection>
      }

      {/* Related Products */}
      {relatedProducts!=null && relatedProducts.hasRelatedProducts && relatedProducts.relatedProducts &&
        <FlatSection>
          <div className="section-title">{relatedProductsTitle}</div>
          <ProductCarousel items={relatedProducts.relatedProducts} />
        </FlatSection>
      }

      {/* Accessory for Products */}
      {relatedProducts!=null && relatedProducts.isProductAccessory && relatedProducts.allProductAccessoryHolders &&
        <FlatSection>
          <div className="section-title">{accessoryForProductsTitle}</div>
          <ProductCarousel items={relatedProducts.allProductAccessoryHolders} />
        </FlatSection>
      }

      {/* Product Reviews */}
      {product.hasProductReviews &&
        <ProductReviews product={product}/>
      }

      {product.showLoadbeeContent &&
        <Script
          src="https://cdn.loadbee.com/js/loadbee_integration.js"
          strategy="lazyOnload"
        />
      }
    </SectionsContain>
  )
}

export default ProductSections;
